import { call, put, takeLatest } from "redux-saga/effects";
import * as actions from "@/store/slices/dashboard.slice";
import * as api from "@/services/dashboard";
import { PayloadAction } from "@reduxjs/toolkit";
import { isBoolean } from "lodash";
import { IDashboard } from "@/services/dashboard/dashboard.type";

// Define action types
export interface GetDashboardDataAction extends PayloadAction<number> {
  type: "dashboard/getDashboardData";
}
export interface GetVdiTransactionDataAction extends PayloadAction<IDashboard> {
  type: "dashboard/getVdiTransactionReport";
}
export interface GetQuikpickProductAction extends PayloadAction<IDashboard> {
  type: "dashboard/getQuikpickProductReport";
}
export interface getSmartCoolerProductsCountAction extends PayloadAction<IDashboard> {
  type: "dashboard/getSmartCoolerProductReport";
}
export interface GetAlertReportAction extends PayloadAction<IDashboard> {
  type: "dashboard/getAlertReport";
}

export type VDIType = {
  date: string;
  total: number;
  accepted: number;
  processed: number;
};

export type AlertsType = {
  date: string;
  count: number;
};

export type quikPickMasterProductsType = {
  total: number;
  cvReady: number;
};

export type smartCoolerProductsType = {
  total: number;
};

export interface IVDITransactions {
  vdiTransactions: any;
  alerts: AlertsType[];
  quikPickMasterProducts: quikPickMasterProductsType;
  smartCoolerProducts: smartCoolerProductsType;
  startDate: string;
  endDate: string;
}


export interface IApiResponse {
  success: boolean;
  message: string;
  data: IVDITransactions;
}

const getErrorPayload = (error: any) => {
  const apiResponse = error?.response?.data;
  return {
    message: apiResponse?.message || error?.message || "Request failed",
    statusCode: apiResponse?.statusCode || error?.response?.status || 500,
    messageDetails: apiResponse?.messageDetails || [],
    errorCode: apiResponse?.errorCode || "DASHBOARD_ERROR",
    success: false
  };
};

// --- Handlers ---

function* getVdiTransactionReport(action: GetVdiTransactionDataAction): Generator {
  try {
    const response: any = yield call(api.fetchVdiTransaction as any, action?.payload);
    if (response?.data && response.data.success) {
      yield put(actions.getVdiTransactionDataSuccess(response.data));
    } else {
      yield put(
        actions.getVdiTransactionDataFailure(
          response?.data || {
            message: "Something went wrong",
            success: false,
          }
        )
      );
    }
  } catch (error: any) {

    yield put(
      actions.getVdiTransactionDataFailure(
        error?.response?.data || {
          message: "Something went wrong",
          success: false,
        }
      )
    );
  }
}

function* getQuickPickProductReport(action: GetQuikpickProductAction): Generator {
  try {
    const response: any = yield call(api.fetchQuikpickProduct, action?.payload);
    if (response?.data && response.data.success) {
      yield put(actions.getQuikpickProductDataSuccess(response.data));
    } else {
      yield put(actions.getQuikpickProductDataFailure(response?.data));
    }
  } catch (error: any) {
    yield put(actions.getQuikpickProductDataFailure(getErrorPayload(error)));
  }
}

function* getSmartCoolerProductDataReport(action: any): Generator {
  try {
    const response: any = yield call(api.fetchSmartCoolerProduct, action.payload);

    if (response?.data?.success) {
      yield put(actions.getSmartCoolerProductDataSuccess(response?.data));
    } else {
      yield put(actions.getSmartCoolerProductDataFailure(response?.data));
    }
  } catch (error: any) {
    const errorData = getErrorPayload(error);
    yield put(actions.getSmartCoolerProductDataFailure(errorData));
  }
}

function* getAlertReport(action: GetAlertReportAction): Generator {
  try {
    const response: any = yield call(api.fetchAlert, action?.payload);
    const resData = response?.data;
    if (resData?.success || resData?.data?.success) {
      yield put(actions.getAlertDataSuccess(resData));
    } else {
      yield put(actions.getAlertDataFailure(resData));
    }
  } catch (error: any) {
    yield put(actions.getAlertDataFailure(getErrorPayload(error)));
  }
}

function* dashboardSaga(): Generator {
  yield takeLatest(actions.getVdiTransactionData.type, getVdiTransactionReport);
  yield takeLatest(actions.getQuikpickProductData.type, getQuickPickProductReport);
  yield takeLatest(actions.getSmartCoolerProductData.type, getSmartCoolerProductDataReport);
  yield takeLatest(actions.getAlertData.type, getAlertReport);
}

export default dashboardSaga;
